import {
    AddIcon, IconLayoutBlocks, TrashOutlineIcon
} from "../../../../../../components/buttons/IconButtons";
import ButtonAction from "../../../../../../components/buttons/ButtonAction";
import ButtonIcon from "../../../../../../components/buttons/ButtonIcon";
import {CustomTextField} from "../../../../../../components/CustomTextField";
import DeleteModal from "../../../../../Modals/DeleteModal";
import React from "react";
import MaterialsInOutApp from "../../../Functionality/EntryAndExitForms/MaterialsInOutApp";
import CustomTabContainer from "../../../../../../components/CustomTabContainer";
import CustomTab from "../../../../../../components/CustomTab";
import {PLANT_MATERIAL_CONTROL_TYPE_TABS} from "../../../Model/Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @param {boolean} required - Indicates whether the materials have to be captured,
 * because the requester leaves them to the guard and the guard writes at the gate
 * what came in or went out of the plant.
 * @param {boolean} disabled - Indicates whether the materials are only read,
 * because the movement they belong to was already registered.
 */
const MaterialsInOut = ({
    materialRequest,
    setMaterialRequest,
    emptyFieldsItems,
    setEmptyFieldsItems,
    required = true,
    disabled = false
}) => {

    const {
        t,
        handleChangeTemp,
        handleSelectItemType,
        showDeleteModal,
        setShowDeleteModal,
        selectedLevelId,
        setSelectedLevelId,
        handleAdd,
        handleDeleteItem,
    } = MaterialsInOutApp({
        materialRequest,
        setMaterialRequest,
        setEmptyFieldsItems
    });

    // The list is marked by whoever validated it, because the material that can
    // be left blank depends on the action: a folio closes with no material at
    // all, but another one is only added once the ones already written are
    // complete. Every material is marked with the field it is missing, so the
    // one that is already complete displays nothing.
    const isMarked = !disabled && emptyFieldsItems;

    /**
     * Tells whether a material is only read.
     *
     * A material that travels with the folio was already registered, so it is
     * read where the list is written and neither changed nor removed there.
     *
     * @param {Object} item - Material being read.
     *
     * @returns {boolean} Returns true when the material is only read.
     */
    const isReadOnly = (item) => disabled || Boolean(item?.registeredId);

    // The materials belong to whoever registered them, so a list that already
    // travels with the folio is only read and no other material is added to it
    const canAdd = !disabled
        && !(materialRequest?.items || []).some((e) => e?.registeredId);


    return (
        <>
            <div className={'row border-radius-8px border-full-shades-300 mt-3'}>
                <div className={"d-flex p-2"}>
                    <div className={"mr-2"}><IconLayoutBlocks/></div>
                    <p className={"fs-14px ibl-font-w-600"}>{t("materials")}</p>
                </div>
                <div className={"border-bottom-shades-300 mb-3"}/>

                <div className={"col-12 mb-4"}>
                    { materialRequest?.items?.map((e, index) => {
                        return <div className={"col-12 p-0 mb-3 background-shades-50 border-radius-8px border-full-shades-100"} key={e?.id ?? index}>
                            <CustomTabContainer
                                activeTab={e?.type}
                                setActiveTab={(type) => {
                                    // A material that is only read keeps the
                                    // movement it was registered with
                                    if (isReadOnly(e)) return;

                                    handleSelectItemType(type, index);
                                }}
                                className='rounded-top border-0 p-3'
                                classNameActions={"flex-grow-1 minw-290px"}
                                emptyFields={isMarked}
                                buttonAction={ <div className={"d-flex align-items-center w-100"}>
                                    <div className={"w-100"}><CustomTextField
                                        required={!disabled && required}
                                        id={'txt_mio_name'}
                                        name={'name'}
                                        placeholder={t('description_of_material_or_item')}
                                        length={128}
                                        value={e?.name}
                                        onChange={(e) => handleChangeTemp(e, index)}
                                        disabled={isReadOnly(e)}
                                        emptyFields={isMarked}
                                        marginBottom={false}
                                        /></div>

                                    { !isReadOnly(e) && materialRequest?.items?.length > 1 &&
                                    <div className={"pl-3"}>
                                        <ButtonIcon
                                            id={'btn_mio_delete'}
                                            Icon={TrashOutlineIcon}
                                            onClick={() => {
                                                setSelectedLevelId(e);
                                                setShowDeleteModal(true);
                                            }}
                                            className='btn-outlined-error-600 h-40px w-40px'
                                            iconProps={'btn-icon-svg-24px'}
                                        />
                                    </div>
                                    }
                                </div> }
                            >
                                {PLANT_MATERIAL_CONTROL_TYPE_TABS.map((item) => (
                                    <CustomTab
                                        key={item.key}
                                        id={item.key}
                                        title={t(item.translation)}
                                        label={t(item.translation)}
                                        icon={item.icon}
                                        selected={e?.type === item.key}
                                    />
                                ))}
                            </CustomTabContainer>
                        </div>
                    })}

                    {/* A list that is only read adds nothing else, because the
                        movement it belongs to was already registered, and one
                        that travels with the folio is not grown at the gate:
                        the materials are written by whoever registered them. */}
                    { canAdd &&
                        <div className='col-12 px-0'>
                            <ButtonAction
                                id='btn_mio_add_new'
                                name='add_new'
                                Icon={AddIcon}
                                splitName={false}
                                className='btn-outlined-secondary-600-00'
                                disabled={false}
                                onClick={() => {
                                    handleAdd();
                                }}
                            />
                        </div>
                    }
                </div>
            </div>


            <DeleteModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setSelectedLevelId(null);
                }}
                data={[selectedLevelId]}
                onClickFn={() => {
                    if (selectedLevelId !== null) {
                        handleDeleteItem(selectedLevelId);
                    }
                    setShowDeleteModal(false);
                    setSelectedLevelId(null);
                }}
                title_modal={t('delete_material')}
                element_modal={t('material')}
            />
        </>
    );
};

export default MaterialsInOut;
